import { Button } from '@edsn/pie-components';
import CloseIcon from '@iconify/icons-material-symbols/close';
import expandMoreRounded from '@iconify/icons-material-symbols/expand-more-rounded';
import ProgressActivityIcon from '@iconify/icons-material-symbols/progress-activity';
import * as Popover from '@radix-ui/react-popover';
import { Command } from 'cmdk';
import type { ComponentProps } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { VariantProps } from '#pie/utils/TailwindUtils';
import { cn, tv } from '#pie/utils/TailwindUtils';
import { Icon } from '../icon/Icon';
import { InputSearch } from '../input-search/InputSearch';
import { styles } from '../input-text/InputText';
import { menuStyles } from '../menu/Menu';
import { Skeleton } from '../skeleton/Skeleton';
import { Stack } from '../stack/Stack';

export type Option = {
  label: string;
  value: string;
};

export interface Props
  extends Omit<ComponentProps<'input'>, 'onChange'>,
    Pick<ComponentProps<typeof Command>, 'shouldFilter'>,
    VariantProps<typeof inputCombobox> {
  disabled?: boolean;
  id?: string;
  isError?: boolean;
  isLoading?: boolean;
  label: string;
  onClear?: () => void;
  onChange?: (value: string) => void;
  onSearch?: (search?: string) => void;
  onLoadMore?: () => void;
  options?: Option[];
  required?: boolean;
  totalCount?: number;
  ref?: React.Ref<HTMLInputElement>;
}

export const InputCombobox = function InputCombobox({
  ref,
  className,
  disabled,
  fullWidth,
  isError,
  isLoading,
  label,
  onChange,
  onLoadMore,
  onSearch,
  options,
  placeholder,
  shouldFilter,
  onClear,
  totalCount,
  value,
  ...props
}: Props) {
  const s = inputCombobox({ disabled, fullWidth, isError });
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState<string>();
  const [selectedOption, setSelectedOption] = useState<Option | undefined>(
    options?.find((option) => option.value === value),
  );

  const onSearchChange = (search?: string) => {
    setSearch(search);
    onSearch?.(search);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedOption(undefined);
    setSearch(undefined);
    onSearch?.(undefined);
    onChange?.('');
    onClear?.();
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen} modal={false}>
      <Popover.Trigger className={s.input()} role="combobox" aria-label={label}>
        <input
          ref={ref}
          type="hidden"
          value={selectedOption?.value || ''}
          {...props}
        />

        {selectedOption?.label || (
          <span className="text-content-disabled">{placeholder}</span>
        )}
        <span className={cn(s.icon(), s.iconEnd(), 'flex items-center')}>
          {isLoading ? (
            <Icon
              icon={ProgressActivityIcon}
              className={cn(s.icon(), s.iconLoading())}
              size={16}
            />
          ) : onClear && selectedOption ? (
            <Button
              aria-label={t('input_text.delete_input.aria-label')}
              onClick={handleClear}
              action="primary"
              emphasis="minimal"
              className="p-0 hover:bg-transparent"
            >
              <Icon
                size={16}
                icon={CloseIcon}
                className="w-5 h-5 rounded-full border-2 text-typography-primary hover:text-action-secondary-enabled"
              />
            </Button>
          ) : (
            <Icon
              icon={expandMoreRounded}
              size={16}
              className="text-action-secondary-enabled"
            />
          )}
        </span>
      </Popover.Trigger>
      {!disabled && (
        <Popover.Content
          className={cn(
            s.content(),
            'max-h-(--radix-popper-available-height) w-(--radix-popper-anchor-width) overflow-y-auto',
          )}
          align="start"
          side="bottom"
          avoidCollisions={false}
          collisionPadding={16}
        >
          <Command
            shouldFilter={shouldFilter}
            className={cn(s.base(), className)}
          >
            <Stack gap="sm">
              <Command.Input
                onValueChange={onSearchChange}
                placeholder={t('common.search.placeholder')}
                value={search}
                asChild
              >
                <InputSearch fullWidth isLoading={isLoading} />
              </Command.Input>
              <Command.List>
                <Command.Group>
                  <Stack divider="line">
                    {isLoading &&
                      !options?.length &&
                      new Array(3).fill(null).map((_, index) => (
                        <div className={s.item()} key={`skeleton-${index}`}>
                          <Skeleton />
                        </div>
                      ))}
                    <Command.Empty
                      className={cn(s.item(), 'text-content-disabled')}
                    >
                      No results.
                    </Command.Empty>
                    {options?.map((option) => (
                      <Command.Item
                        value={option.value}
                        key={option.value}
                        className={cn(
                          s.item(),
                          'data-[selected=true]:bg-surface-minimal-teal',
                        )}
                        onSelect={() => {
                          setOpen(false);
                          onChange?.(option.value);
                          setSelectedOption(option);
                          onSearchChange(option.label);
                        }}
                      >
                        {option.label}
                      </Command.Item>
                    ))}
                    {!!options &&
                      !!totalCount &&
                      !!onLoadMore &&
                      options?.length < totalCount && (
                        <Button
                          action="primary"
                          emphasis="minimal"
                          className="-mb-1 rounded-none border-0 p-3"
                          onClick={onLoadMore}
                        >
                          {t('common.show_more')}
                        </Button>
                      )}
                  </Stack>
                </Command.Group>
              </Command.List>
            </Stack>
          </Command>
        </Popover.Content>
      )}
    </Popover.Root>
  );
};

const inputCombobox = tv({
  base: '',
  extend: styles,
  slots: {
    content: menuStyles().content(),
    icon: menuStyles().icon(),
    iconLoading: 'animate-spin',
    input:
      'data-[state=closed]:border-border-subtle-enabled data-[state=open]:border-border-subtle-enabled flex justify-between pl-4 pr-2 py-[11px] items-center border active:border-border-subtle-active gap-2 data-[placeholder]:text-content-disabled max-h-[48px] outline-none',
    item: cn(menuStyles().item()),
    value: 'overflow-hidden text-ellipsis whitespace-nowrap',
  },
  variants: {
    isError: {
      false: {
        input:
          'focus:border-border-subtle-enabled focus:shadow-none focus:ring-0 data-[state=closed]:focus:border-border-subtle-focus data-[state=open]:focus:border-border-subtle-enabled',
      },
    },
  },
});
